import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mic, MicOff, Volume2, VolumeX, Camera, X, HelpCircle,
  Loader2, Sun, Heart, Briefcase, Coins
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import ShubhHeader from '../components/shubh/ShubhHeader';
import ShubhBottomNav from '../components/shubh/ShubhBottomNav';

import { BACKEND_URL } from '../lib/backend';

const API_URL = BACKEND_URL;

const VedicGuruPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [kundaliImage, setKundaliImage] = useState(null);
  const [kundaliPreview, setKundaliPreview] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [answer, setAnswer] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const currentAudioRef = useRef(null);
  const fileInputRef = useRef(null);

  const getToken = () => localStorage.getItem('auth_token');
  const lang = language === 'hi' ? 'hindi' : 'english';

  // Fetch user profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await fetch(`${API_URL}/api/profiles`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        if (response.ok) {
          const data = await response.json();
          const list = Array.isArray(data) ? data : (data.profiles || []);
          if (list.length > 0) {
            setUserProfile(list[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };
    fetchProfile();
  }, []);

  // Play audio response
  const playAudio = (base64Audio) => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
    }
    
    const audio = new Audio(`data:audio/mpeg;base64,${base64Audio}`);
    currentAudioRef.current = audio;
    
    audio.onplay = () => setIsSpeaking(true);
    audio.onended = () => setIsSpeaking(false);
    audio.onerror = () => setIsSpeaking(false);
    
    audio.play().catch(err => {
      console.error('Audio playback error:', err);
      setIsSpeaking(false);
    });
  };

  const stopAudio = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
      setIsSpeaking(false);
    }
  };

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await sendVoiceMessage(audioBlob);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      
    } catch (error) {
      console.error('Microphone error:', error);
      toast.error(lang === 'hindi' ? 'माइक्रोफ़ोन एक्सेस नहीं मिला' : 'Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Send voice message
  const sendVoiceMessage = (audioBlob) => {
    setIsLoading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const base64Audio = reader.result.split(',')[1];
        const profileContext = userProfile
          ? `User: ${userProfile.name}, DOB: ${userProfile.date_of_birth}, Time: ${userProfile.time_of_birth || 'Unknown'}, Place: ${userProfile.place_of_birth || 'Unknown'}.`
          : '';

        const response = await fetch(`${API_URL}/api/vedic-guru/voice`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`
          },
          body: JSON.stringify({
            audio_base64: base64Audio,
            language: lang,
            session_id: sessionId,
            kundali_image_base64: kundaliImage,
            profile_context: profileContext
          })
        });
        if (!response.ok) throw new Error('Failed to process voice');
        const data = await response.json();

        if (data.session_id) setSessionId(data.session_id);
        setKundaliImage(null);
        setKundaliPreview(null);
        setAnswer({ question: data.user_transcript, text: data.text_response, audio: data.audio_base64 });
        if (data.audio_base64) playAudio(data.audio_base64);
      } catch (error) {
        console.error('Voice error:', error);
        toast.error(lang === 'hindi' ? 'कुछ गड़बड़ हुई' : 'Something went wrong');
      } finally {
        setIsLoading(false);
      }
    };
    reader.readAsDataURL(audioBlob);
  };

  // Handle quick question
  const handleQuickQuestion = async (question) => {
    setIsLoading(true);
    
    try {
      const profileContext = userProfile 
        ? `User: ${userProfile.name}, DOB: ${userProfile.date_of_birth}, Time: ${userProfile.time_of_birth || 'Unknown'}, Place: ${userProfile.place_of_birth || 'Unknown'}.`
        : '';
      
      const response = await fetch(`${API_URL}/api/vedic-guru/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          message: `${profileContext} Question: ${question}`,
          language: lang,
          session_id: sessionId,
          kundali_image_base64: kundaliImage
        })
      });
      
      if (!response.ok) throw new Error('Failed to get response');
      
      const data = await response.json();
      
      if (data.session_id) setSessionId(data.session_id);
      
      setKundaliImage(null);
      setKundaliPreview(null);
      setAnswer({ question, text: data.text_response, audio: data.audio_base64 });
      
      if (data.audio_base64) {
        playAudio(data.audio_base64);
      }
      
    } catch (error) {
      console.error('Chat error:', error);
      toast.error(lang === 'hindi' ? 'कुछ गड़बड़ हुई' : 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Kundali upload
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error(lang === 'hindi' ? 'फ़ाइल बहुत बड़ी है' : 'File too large');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      setKundaliImage(base64);
      setKundaliPreview(reader.result);
      toast.success(lang === 'hindi' ? 'कुंडली अपलोड हो गई' : 'Kundali uploaded');
    };
    reader.readAsDataURL(file);
  };

  const quickQuestions = lang === 'hindi' ? [
    { text: 'आज का दिन कैसा है?', icon: Sun, tone: 'saffron' },
    { text: 'मेरी शादी कब होगी?', icon: Heart, tone: 'lotus' },
    { text: 'करियर में क्या करूं?', icon: Briefcase, tone: 'sky' },
    { text: 'पैसों की समस्या', icon: Coins, tone: 'tulsi' },
  ] : [
    { text: "How's my day today?", icon: Sun, tone: 'saffron' },
    { text: 'When will I get married?', icon: Heart, tone: 'lotus' },
    { text: 'Career guidance please', icon: Briefcase, tone: 'sky' },
    { text: 'Financial problems', icon: Coins, tone: 'tulsi' },
  ];

  const TONES = {
    saffron: { bg: 'rgba(245, 185, 66, 0.16)', fg: '#B7791F' },
    lotus: { bg: 'rgba(246, 199, 211, 0.45)', fg: '#B5476A' },
    sky: { bg: 'rgba(118, 183, 242, 0.18)', fg: '#2F6FB0' },
    tulsi: { bg: 'rgba(40, 122, 96, 0.12)', fg: '#287A60' },
  };

  const status = isSpeaking
    ? { label: lang === 'hindi' ? 'बोल रहे हैं...' : 'Speaking...', bg: '#C84B32' }
    : isLoading
      ? { label: lang === 'hindi' ? 'सोच रहे हैं...' : 'Thinking...', bg: '#E5A832' }
      : isRecording
        ? { label: lang === 'hindi' ? 'सुन रहे हैं...' : 'Listening...', bg: '#287A60' }
        : { label: lang === 'hindi' ? 'गुरु जी' : 'Guru Ji', bg: '#282622' };

  return (
    <div className="min-h-screen relative overflow-hidden pb-32" style={{ background: 'linear-gradient(180deg, #FFF9EF 0%, #FCE9D9 100%)' }} data-testid="vedic-guru-page">
      <ShubhHeader title={lang === 'hindi' ? 'वैदिक गुरु' : 'Vedic Guru'} showBack onBack={() => navigate('/dashboard')} />

      {/* Rotating mandala backdrop */}
      <div className="absolute left-1/2 top-24 -translate-x-1/2 w-[520px] h-[520px] pointer-events-none opacity-60">
        <svg viewBox="0 0 200 200" className="w-full h-full animate-spin-slow" style={{ animationDuration: '60s' }}>
          {[...Array(12)].map((_, i) => (
            <ellipse key={i} cx="100" cy="100" rx="90" ry="28" fill="none" stroke="rgba(245,185,66,0.25)" strokeWidth="0.5" transform={`rotate(${i * 15} 100 100)`} />
          ))}
          <circle cx="100" cy="100" r="96" fill="none" stroke="rgba(200,75,50,0.15)" strokeWidth="0.6" strokeDasharray="2 3" />
        </svg>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 z-[95] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowHelp(false)} data-testid="vedic-guru-help-modal">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <h2 className="font-['Fraunces'] text-2xl text-[#282622] mb-5 text-center">
              {lang === 'hindi' ? 'कैसे पूछें?' : 'How to Ask?'}
            </h2>
            <div className="space-y-4 text-[#4A4640] text-sm">
              {[
                lang === 'hindi' ? 'बड़े बटन को दबाकर रखें और अपना सवाल बोलें' : 'Hold the big button and speak your question',
                lang === 'hindi' ? 'बटन छोड़ें - गुरु जी आपको जवाब देंगे' : 'Release the button - Guru Ji will answer you',
                lang === 'hindi' ? 'या नीचे के सवालों पर टैप करें' : 'Or tap on quick questions below',
                lang === 'hindi' ? 'कुंडली की फोटो भी अपलोड कर सकते हैं' : 'You can also upload your Kundali photo',
              ].map((t, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <span className="w-7 h-7 shrink-0 rounded-full bg-[#FCE9D9] text-[#C84B32] font-semibold flex items-center justify-center text-xs">{i + 1}</span>
                  <p className="pt-1">{t}</p>
                </div>
              ))}
            </div>
            <button
              onClick={() => setShowHelp(false)}
              className="w-full mt-6 py-3 bg-gradient-to-r from-[#C84B32] to-[#E85D43] text-white rounded-full font-semibold"
              data-testid="vedic-guru-help-close"
            >
              {lang === 'hindi' ? 'समझ गया!' : 'Got it!'}
            </button>
          </div>
        </div>
      )}

      <div className="relative z-10 flex flex-col items-center px-5 pt-6 max-w-lg mx-auto">
        <button
          onClick={() => setShowHelp(true)}
          className="self-end flex items-center gap-1.5 text-xs font-medium text-[#6B6560] bg-white/80 border border-[#282622]/10 rounded-full px-3 py-1.5 hover:text-[#C84B32] transition-colors"
          data-testid="vedic-guru-help-btn"
        >
          <HelpCircle className="w-4 h-4" />
          {lang === 'hindi' ? 'मदद' : 'How it works'}
        </button>

        {/* Guru Avatar */}
        <div className="relative mt-2 mb-8">
          <div className={`absolute inset-0 rounded-full blur-2xl transition-all duration-500 ${isSpeaking ? 'scale-150 opacity-100' : 'scale-110 opacity-60'}`} style={{ background: 'radial-gradient(circle, rgba(245,185,66,0.7), rgba(200,75,50,0.25))' }} />
          <div className={`relative w-36 h-36 rounded-full flex items-center justify-center shadow-2xl transition-transform duration-300 ${isSpeaking ? 'scale-110' : ''}`} style={{ background: 'linear-gradient(140deg, #F5B942 0%, #E8913A 45%, #C84B32 100%)', boxShadow: '0 18px 50px rgba(200,75,50,0.35)' }}>
            <span className="text-6xl text-white font-['Fraunces'] leading-none" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.15)' }}>ॐ</span>
            {isSpeaking && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="w-1 bg-white rounded-full animate-bounce" style={{ height: '12px', animationDelay: `${i * 0.1}s`, animationDuration: '0.4s' }} />
                ))}
              </div>
            )}
          </div>
          <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold text-white whitespace-nowrap shadow-md" style={{ background: status.bg }} data-testid="vedic-guru-status">
            {status.label}
          </div>
        </div>

        {userProfile && (
          <p className="text-[#6B6560] text-sm mb-2 text-center" data-testid="vedic-guru-greeting">
            {lang === 'hindi' ? `नमस्कार ${userProfile.name} जी, अपना सवाल पूछें` : `Namaste ${userProfile.name}, ask your question`}
          </p>
        )}

        <div className="text-center mb-6">
          <p className="font-['Fraunces'] text-2xl text-[#282622] mb-1">
            {isRecording
              ? (lang === 'hindi' ? 'बोलें...' : 'Speak now...')
              : isSpeaking
                ? (lang === 'hindi' ? 'सुनें...' : 'Listen...')
                : (lang === 'hindi' ? 'दबाकर बोलें' : 'Hold & Speak')}
          </p>
          {!isRecording && !isSpeaking && (
            <p className="text-[#8A8279] text-xs">
              {lang === 'hindi' ? 'बटन दबाएं, सवाल पूछें, छोड़ें' : 'Press button, ask question, release'}
            </p>
          )}
        </div>

        {/* Main Mic Button */}
        <button
          onMouseDown={startRecording}
          onMouseUp={stopRecording}
          onMouseLeave={isRecording ? stopRecording : undefined}
          onTouchStart={startRecording}
          onTouchEnd={stopRecording}
          disabled={isLoading || isSpeaking}
          data-testid="vedic-guru-mic-btn"
          className={`relative w-24 h-24 rounded-full flex items-center justify-center transition-transform duration-300 mb-6 ${
            isRecording ? 'scale-125' : isSpeaking || isLoading ? 'cursor-not-allowed opacity-50' : 'hover:scale-105 active:scale-110'
          }`}
          style={{
            background: isRecording ? '#287A60' : 'linear-gradient(135deg, #C84B32 0%, #E85D43 100%)',
            boxShadow: isRecording ? '0 0 40px rgba(40,122,96,0.5)' : '0 12px 32px rgba(200,75,50,0.4)'
          }}
        >
          {isLoading ? <Loader2 className="w-10 h-10 text-white animate-spin" /> : isRecording ? <MicOff className="w-10 h-10 text-white" /> : <Mic className="w-10 h-10 text-white" />}
          {isRecording && (
            <>
              <div className="absolute inset-0 rounded-full border-4 border-green-600 animate-ping opacity-40" />
              <div className="absolute inset-[-8px] rounded-full border-2 border-green-600/30 animate-pulse" />
            </>
          )}
        </button>

        {isSpeaking && (
          <button
            onClick={stopAudio}
            className="flex items-center gap-2 px-4 py-2 bg-red-50 hover:bg-red-100 border border-red-200 rounded-full text-red-600 text-sm mb-6 transition-colors"
            data-testid="vedic-guru-stop-btn"
          >
            <VolumeX className="w-4 h-4" />
            {lang === 'hindi' ? 'रोकें' : 'Stop'}
          </button>
        )}

        {kundaliPreview && (
          <div className="relative mb-6">
            <img src={kundaliPreview} alt="Kundali" className="h-20 w-20 object-cover rounded-xl border-2 border-[#F5B942]" />
            <button
              onClick={() => { setKundaliImage(null); setKundaliPreview(null); }}
              className="absolute -top-2 -right-2 w-6 h-6 bg-[#C84B32] rounded-full flex items-center justify-center text-white"
              data-testid="vedic-guru-remove-kundali"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoading || isSpeaking || isRecording}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-[#282622]/10 rounded-full text-[#4A4640] text-sm mb-8 shadow-sm hover:border-[#F5B942] hover:text-[#C84B32] transition-colors disabled:opacity-50"
          data-testid="vedic-guru-upload-kundali"
        >
          <Camera className="w-4 h-4" />
          {lang === 'hindi' ? 'कुंडली अपलोड करें' : 'Upload Kundali'}
        </button>

        {/* Latest answer */}
        {answer && (
          <div className="w-full bg-white rounded-2xl border border-[#F5B942]/40 shadow-sm p-5 mb-8 text-left" data-testid="vedic-guru-answer">
            {answer.question && (
              <p className="text-xs text-[#8A8279] mb-2">
                {lang === 'hindi' ? 'आपका सवाल: ' : 'You asked: '}<span className="text-[#4A4640]">{answer.question}</span>
              </p>
            )}
            <p className="text-[#282622] text-sm leading-relaxed whitespace-pre-line">{answer.text}</p>
            {answer.audio && !isSpeaking && (
              <button
                onClick={() => playAudio(answer.audio)}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#C84B32]"
                data-testid="vedic-guru-replay-btn"
              >
                <Volume2 className="w-4 h-4" />
                {lang === 'hindi' ? 'फिर से सुनें' : 'Play again'}
              </button>
            )}
          </div>
        )}

        {/* Quick Questions */}
        <div className="w-full">
          <p className="text-[#8A8279] text-xs uppercase tracking-[0.14em] text-center mb-3 font-semibold">
            {lang === 'hindi' ? 'या ये सवाल पूछें' : 'Or ask these'}
          </p>
          <div className="grid grid-cols-2 gap-3">
            {quickQuestions.map((q, idx) => {
              const Icon = q.icon;
              const tone = TONES[q.tone];
              return (
                <button
                  key={idx}
                  onClick={() => handleQuickQuestion(q.text)}
                  disabled={isLoading || isSpeaking || isRecording}
                  className="group p-3.5 bg-white border border-[#282622]/10 rounded-2xl text-[#282622] text-sm font-medium transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-[#F5B942]/60 disabled:opacity-50 text-left flex items-center gap-3"
                  data-testid={`vedic-guru-quick-q-${idx}`}
                >
                  <span className="w-9 h-9 shrink-0 rounded-xl flex items-center justify-center" style={{ background: tone.bg }}>
                    <Icon className="w-4.5 h-4.5" style={{ color: tone.fg, width: 18, height: 18 }} />
                  </span>
                  <span className="leading-snug">{q.text}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <ShubhBottomNav />
    </div>
  );
};

export default VedicGuruPage;
